import { useCallback, useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiService } from 'api/services/ApiService';
import { AttioQueryKeys, COMPANY_NAME_KEY, DESC, STATUS_KEY, VALUATION_DATE_KEY, VALUATION_FEE_KEY } from 'common/constants/analyst-dashboards';
import { useStore } from 'common/store';
import { UseStoreValues } from 'common/types/store';
import { AttioSortDirection } from 'dashboard409a/common/constants/analyst-dashboard';
import { EngagementManagerOption } from 'dashboard409a/components/ValuationsTable/ValuationsTableRow/ValuationsTableRow.types';
import { ValuationStatus } from 'dashboard409a/pages/Workboard/common/types';
import { AttioTeamService } from 'dashboard409a/services/attio/AttioTeamService';
import { getAttioUserRecordId } from 'dashboard409a/services/attio-common';
import { getAllAttioValuations, loadAllBulkData, loadBulkNotes } from 'dashboard409a/services/attio-dashboard';
import { AttioConsultantCacheData, AttioDealCacheData, AttioEngagementManagerCacheData, SimplifiedAttioValuation } from 'dashboard409a/services/attio.types';
import { Note } from 'dashboard409a/services/models.types';
import { useLocalStorage } from 'services/hooks/useLocalStorage';
import { getObjectValue, Logger } from 'utilities';
import { UseValuationsWithBulkDataProps, UseValuationsWithBulkDataReturn } from './useValuationsWithBulkData.types';

// Constants
const DEFAULT_ORDER_BY = VALUATION_DATE_KEY;
const DEFAULT_ORDER = DESC;
const USER_RECORD_CACHE_TIME = 60 * 60 * 1000; // 60 minutes
const VALUATIONS_SORT_KEY = 'valuationsSort';
// Generate sorted status options from enum values in workflow order
const SORTED_STATUS_OPTIONS = Object.values(ValuationStatus).map((status, index) => ({
  value: status,
  weight: index + 1
}))

const getStatusWeight = (status: string): number => {
  const statusOption = SORTED_STATUS_OPTIONS.find(option => option.value === status);
  return statusOption?.weight || 0;
};

// Local sorting function - moved outside component to avoid recreation
const sortValuationsLocally = (valuations: SimplifiedAttioValuation[], sortBy: string, sortOrder: AttioSortDirection) => {
  const getValue = (record: SimplifiedAttioValuation): string | number => {
    switch (sortBy) {
      case COMPANY_NAME_KEY:
        return record[sortBy] || '';
      case STATUS_KEY:
        return getStatusWeight(record[sortBy] || '');
      case VALUATION_DATE_KEY:
        return record.valuation_date ? new Date(record.valuation_date).getTime() : 0;
      case VALUATION_FEE_KEY:
        return record.valuation_fee ? Number(record.valuation_fee.replaceAll(/[^0-9.-]/g, '')) || 0 : 0;
      default:
        return '';
    }
  };
  localStorage.setItem(VALUATIONS_SORT_KEY, JSON.stringify({ sortBy, sortOrder }));

  const sorted = [...valuations].sort((a, b) => {
    const aValue = getValue(a);
    const bValue = getValue(b);

    if (aValue === bValue) {
      return 0;
    }
    return (aValue < bValue ? -1 : 1) * (sortOrder === 'asc' ? 1 : -1);
  });
  return sorted;
};

export const useValuationsWithBulkData = ({
  filters,
  orderBy = DEFAULT_ORDER_BY,
  order = DEFAULT_ORDER,
  pauseBatchLoading = false,
}: UseValuationsWithBulkDataProps): UseValuationsWithBulkDataReturn => {
  const queryClient = useQueryClient();
  
  // State for data loading
  const [displayedValuations, setDisplayedValuations] = useState<SimplifiedAttioValuation[]>([]);
  const [isLoadingAdditionalData, setIsLoadingAdditionalData] = useState(false);
  const [isInitialLoadComplete, setIsInitialLoadComplete] = useState(false);
  const [selectedUserEmail, setSelectedUserEmail] = useLocalStorage<string>('selectedUserEmail', '');
  const [totalCount, setTotalCount] = useState(0);
  const [allValuations, setAllValuations] = useState<SimplifiedAttioValuation[]>([]);
  const [additionalDataLoaded, setAdditionalDataLoaded] = useState(false);
  const [otherConsultantsCache, setOtherConsultantsCache] = useState<Map<string, AttioConsultantCacheData>>(new Map());
  const [engagementManagersCache, setEngagementManagersCache] = useState<Map<string, AttioEngagementManagerCacheData>>(new Map());
  const [dealCache, setDealCache] = useState<Map<string, AttioDealCacheData>>(new Map());
  const [notesCache, setNotesCache] = useState<Map<string, Note | null>>(new Map());
  const [statusJustChanged, setStatusJustChanged] = useState(false);
  // AbortController for cancelling bulk loading requests
  const bulkAbortControllerRef = useRef<AbortController | null>(null);
  // Get user email from store
  const [{ user }] = useStore() as unknown as UseStoreValues;
  const { email: userEmail = '', is_superuser = false } = getObjectValue(user);

  // Get user record ID
  // For superusers: use selectedUserEmail if provided, otherwise fall back to userEmail
  // For non-superusers: always use userEmail
  // Never use empty string - always fall back to userEmail if selectedUserEmail is empty
  const effectiveUserEmail = is_superuser && selectedUserEmail ? selectedUserEmail : userEmail;
  const { data: userRecordId, isLoading: isLoadingUserRecord } = useQuery({
    queryKey: [AttioQueryKeys.USER_RECORD_ID, effectiveUserEmail],
    queryFn: () => getAttioUserRecordId(effectiveUserEmail),
    staleTime: USER_RECORD_CACHE_TIME,
    retry: false,
    enabled: !!effectiveUserEmail && effectiveUserEmail.trim() !== '', // Only run if userEmail exists and is not empty
  });

  /// Get users for select list
  const { data: staffUsers = [], isLoading: isLoadingStaffUsers } = useQuery({
    queryKey: ['users-list', 'staff'],
    queryFn: () => ApiService.apiUsersStaffList(),
    enabled: is_superuser,
    staleTime: USER_RECORD_CACHE_TIME,
    retry: false,
  });

  // Reset all state when userRecordId changes (e.g., when selectedUserEmail changes)
  useEffect(() => {
    // Only reset if userRecordId actually changed (not on initial mount when it's undefined)
    if (userRecordId !== undefined) {
      setDisplayedValuations([]);
      setAllValuations([]);
      setIsInitialLoadComplete(false);
      setAdditionalDataLoaded(false);
      setOtherConsultantsCache(new Map());
      setEngagementManagersCache(new Map());
      setDealCache(new Map());
      setNotesCache(new Map());
      setTotalCount(0);
    }
  }, [userRecordId]);

  // Bulk load all additional data in a single batch
  const loadAllAdditionalData = useCallback(async () => {
    if (allValuations.length === 0 || additionalDataLoaded) {
      return;
    }

    // Create new AbortController for this bulk load
    const abortController = new AbortController();
    bulkAbortControllerRef.current = abortController;
    const { signal } = abortController;

    setIsLoadingAdditionalData(true);

    try {
      // Collect all unique IDs from ALL valuations
      const consultantIds = [...new Set(
        allValuations
          .filter(record => record.other_consultant_id)
          .map(record => record.other_consultant_id!)
      )];

      const managerIds = [...new Set(
        allValuations
          .filter(record => record.engagement_manager_id)
          .map(record => record.engagement_manager_id!)
      )];

      const dealIds = [...new Set(
        allValuations
          .filter(record => record.deal_id)
          .map(record => record.deal_id!)
      )];

      // Load ALL data in 3 bulk requests (instead of hundreds of individual requests)
      const { consultants, managers, deals } = await loadAllBulkData(
        consultantIds,
        managerIds,
        dealIds,
        signal
      );

      // Check if aborted before updating state
      if (signal.aborted) {
        return;
      }

      // Cache in React Query for consistency with existing code
      consultants.forEach((data, id) => {
        queryClient.setQueryData([AttioQueryKeys.OTHER_CONSULTANT, id], data);
      });

      managers.forEach((data, id) => {
        queryClient.setQueryData([AttioQueryKeys.ENGAGEMENT_MANAGER, id], data);
      });

      // Update caches
      setOtherConsultantsCache(consultants);
      setEngagementManagersCache(managers);
      setDealCache(deals);

      // Add default values for records without IDs
      const defaultData = { name: '-', imageSrc: '-' };
      if (allValuations.some(record => !record.other_consultant_id)) {
        consultants.set('no-id', defaultData);
        setOtherConsultantsCache(prev => new Map([...prev, ['no-id', defaultData]]));
      }
      if (allValuations.some(record => !record.engagement_manager_id)) {
        managers.set('no-id', defaultData);
        setEngagementManagersCache(prev => new Map([...prev, ['no-id', defaultData]]));
      }
      if (allValuations.some(record => !record.deal_id)) {
        deals.set('no-id', { value: '-', due: '-', stage: '-' });
        setDealCache(prev => new Map([...prev, ['no-id', { value: '-', due: '-', stage: '-' }]]));
      }

      // Update displayed valuations with the loaded data
      setDisplayedValuations(prev =>
        prev.map(record => {
          const consultant = record.other_consultant_id
            ? consultants.get(record.other_consultant_id) || defaultData
            : defaultData;

          const manager = record.engagement_manager_id
            ? managers.get(record.engagement_manager_id) || defaultData
            : defaultData;

          const deal = record.deal_id
            ? deals.get(record.deal_id)
            : { value: '-', due: '-', stage: '-' };

          const note = queryClient.getQueryData<Note | null>([
            AttioQueryKeys.BULK_NOTES,
            record.record_id,
          ]);

          return {
            ...record,
            other_consultant: consultant.name,
            other_consultant_image_src: consultant.imageSrc,
            engagement_manager: manager.name,
            engagement_manager_image_src: manager.imageSrc,
            deal: deal?.value || '-',
            deal_due: deal?.due || '-',
            deal_stage: deal?.stage || '-',
            note: note || null,
            is_loading_additional_data: false,
          };
        })
      );

      setAdditionalDataLoaded(true);

    } catch (error) {
      // Don't log AbortError - it's expected when modal opens
      if (error instanceof DOMException && error.name === 'AbortError') {
        return;
      }
      Logger.error('Error loading additional data', error);
    } finally {
      // Only clear loading state if not aborted
      if (!signal.aborted) {
        setIsLoadingAdditionalData(false);
      }
      // Clear the ref if this was the current controller
      if (bulkAbortControllerRef.current === abortController) {
        bulkAbortControllerRef.current = null;
      }
    }
  }, [allValuations, additionalDataLoaded, queryClient]);


  // Load all valuations (1000 at a time) - sorting applied locally
  const { data: allValuationsData, isLoading: isLoadingAllValuations } = useQuery({
    queryKey: ['valuations-all', userRecordId, filters],
    queryFn: () => getAllAttioValuations({
      userRecordId: userRecordId!,
      filters,
    }),
    enabled: !!userRecordId,
    retry: false,
    refetchOnWindowFocus: false,
    staleTime: 10 * 60 * 1000, // 10 minutes cache
    gcTime: 30 * 60 * 1000, // 30 minutes garbage collection
  });

  // Load initial data when all valuations are loaded
  useEffect(() => {
    if (allValuationsData) {
      if (allValuationsData.data && allValuationsData.data.length > 0) {
        // Store all valuations and set initial display
        setAllValuations(allValuationsData.data);
        setTotalCount(allValuationsData.totalCount || allValuationsData.data.length);
        
        if (displayedValuations.length === 0) {
          // Show loading state first
          const allRecordsWithLoading = allValuationsData.data.map((record: SimplifiedAttioValuation) => ({
            ...record,
            is_loading_additional_data: true,
            other_consultant: null,
            other_consultant_image_src: null,
            engagement_manager: null,
            engagement_manager_image_src: null,
            notes: null, // Will be loaded
            deal: null,
            deal_due: null,
            deal_stage: null,
          }));
          setDisplayedValuations(allRecordsWithLoading);
        }
      } else {
        // No data case - set empty arrays and mark as complete
        setAllValuations([]);
        setDisplayedValuations([]);
        setTotalCount(0);
        setIsInitialLoadComplete(true);
      }
    }
  }, [allValuationsData, displayedValuations.length]);

  // Apply local sorting when data or sorting parameters change
  useEffect(() => {
    if (allValuations.length > 0) {
      // Determine if we should apply delay: only when sorting by status AND status was just changed
      const isSortingByStatus = orderBy === STATUS_KEY;
      const shouldDelay = isSortingByStatus && statusJustChanged;
      const delay = shouldDelay ? 5000 : 0;
      
      const performSort = () => {
        // Recalculate sorting to ensure we have latest values
        let sortedValuations: SimplifiedAttioValuation[] = [];
        const localSort = localStorage.getItem(VALUATIONS_SORT_KEY);
        
        if (orderBy !== DEFAULT_ORDER_BY || order !== DEFAULT_ORDER) {
          sortedValuations = sortValuationsLocally(allValuations, orderBy, order);
        } else if (localSort) {
          const { sortBy, sortOrder } = JSON.parse(localSort);
          sortedValuations = sortValuationsLocally(allValuations, sortBy, sortOrder);
        } else {
          sortedValuations = sortValuationsLocally(allValuations, orderBy, order);
        }
        
        setDisplayedValuations(prevDisplayedValuations => {
          // Create a map of existing data by record_id for quick lookup
          const existingDataMap = new Map(
            prevDisplayedValuations.map(record => [
              record.record_id, 
              {
                other_consultant: record.other_consultant,
                other_consultant_image_src: record.other_consultant_image_src,
                engagement_manager: record.engagement_manager,
                engagement_manager_image_src: record.engagement_manager_image_src,
                note: record.note,
                deal: record.deal,
                deal_due: record.deal_due,
                deal_stage: record.deal_stage,
                is_loading_additional_data: record.is_loading_additional_data,
              }
            ])
          );
          
          return sortedValuations.map(record => {
            const existingData = existingDataMap.get(record.record_id);
            const cachedDeal = record.deal_id ? dealCache.get(record.deal_id) : undefined;
            
            // Try to get cached data from React Query
            const cachedNote = queryClient.getQueryData<Note | null>([
              AttioQueryKeys.BULK_NOTES,
              record.record_id,
            ]);
            const cachedOtherConsultant = record.other_consultant_id
              ? queryClient.getQueryData<AttioConsultantCacheData>([
                AttioQueryKeys.OTHER_CONSULTANT,
                record.other_consultant_id,
              ])
              : { name: '-', imageSrc: '-' };
            const cachedEngagementManager = record.engagement_manager_id
              ? queryClient.getQueryData<AttioEngagementManagerCacheData>([
                AttioQueryKeys.ENGAGEMENT_MANAGER,
                record.engagement_manager_id,
              ])
              : { name: '-', imageSrc: '-' };
            
            // Use existing data if available, otherwise use cached data, otherwise use defaults
            const otherConsultant = existingData?.other_consultant ?? (cachedOtherConsultant?.name || '-');
            const otherConsultantImageSrc = existingData?.other_consultant_image_src ?? (cachedOtherConsultant?.imageSrc || '-');
            const engagementManager = existingData?.engagement_manager ?? (cachedEngagementManager?.name || '-');
            const engagementManagerImageSrc = existingData?.engagement_manager_image_src ?? (cachedEngagementManager?.imageSrc || '-');
            const note = existingData?.note ?? (cachedNote || null);
            const deal = existingData?.deal ?? cachedDeal?.value ?? record.deal ?? '-';
            const dealDue = existingData?.deal_due ?? cachedDeal?.due ?? record.deal_due ?? '-';
            const dealStage = existingData?.deal_stage ?? cachedDeal?.stage ?? record.deal_stage ?? '-';
            
            // Determine if we should show loading state
            const hasNotesData = note !== undefined;
            const hasOtherConsultantData = record.other_consultant_id 
              ? (cachedOtherConsultant !== null && cachedOtherConsultant !== undefined) 
              : true;
            const hasEngagementManagerData = record.engagement_manager_id 
              ? (cachedEngagementManager !== null && cachedEngagementManager !== undefined) 
              : true;
            const isRecordLoadingAdditionalData = existingData?.is_loading_additional_data ?? !(hasNotesData && hasOtherConsultantData && hasEngagementManagerData);
            
            if (existingData) {
              return {
                ...record,
                other_consultant: otherConsultant,
                other_consultant_image_src: otherConsultantImageSrc,
                engagement_manager: engagementManager,
                engagement_manager_image_src: engagementManagerImageSrc,
                note,
                deal,
                deal_due: dealDue,
                deal_stage: dealStage,
                is_loading_additional_data: isRecordLoadingAdditionalData
              };
            }
            return {
              ...record,
              is_loading_additional_data: isRecordLoadingAdditionalData,
              other_consultant: otherConsultant,
              other_consultant_image_src: otherConsultantImageSrc,
              engagement_manager: engagementManager,
              engagement_manager_image_src: engagementManagerImageSrc,
              note,
              deal,
              deal_due: dealDue,
              deal_stage: dealStage,
            };
          });
        });
        
        // Reset the status change flag after sorting
        if (shouldDelay) {
          setStatusJustChanged(false);
        }
      };
      
      const timeoutId = setTimeout(performSort, delay);
      
      return () => {
        clearTimeout(timeoutId);
      };
    }
    return undefined;
  }, [allValuations, orderBy, order, queryClient, dealCache, statusJustChanged]);

  // Reset status change flag when sorting changes to something other than status
  useEffect(() => {
    if (orderBy !== STATUS_KEY && statusJustChanged) {
      setStatusJustChanged(false);
    }
  }, [orderBy, statusJustChanged]);

  // Helper function to update displayed valuations with notes
  const updateDisplayedValuationsWithNotes = useCallback((notesMap: Map<string, Note | null>) => {
    setDisplayedValuations(prev => {
      const updatedRecords = prev.map(record => {
        const note = notesMap.get(record.record_id) || null;
        return {
          ...record,
          note,
        };
      });
      
      // Return updated records without re-sorting to preserve current order
      return updatedRecords;
    });
  }, []);

  // Helper function to set default notes
  const setDefaultNotes = useCallback(() => {
    setDisplayedValuations(prev => {
      const updatedRecords = prev.map(record => ({
        ...record,
        note: null,
      }));
      
      // Return updated records without re-sorting to preserve current order
      return updatedRecords;
    });
  }, []);

  // Load notes in bulk using React Query for caching
  const { data: bulkNotesData, isLoading: isLoadingBulkNotes } = useQuery<Map<string, Note | null>>({
    queryKey: [AttioQueryKeys.BULK_NOTES, allValuations.map(record => record.record_id).sort((a, b) => a.localeCompare(b)).join(',')],
    queryFn: async () => {
      if (allValuations.length === 0) {
        return new Map<string, Note | null>();
      }
      const valuationIds = allValuations.map(record => record.record_id);
      return loadBulkNotes(valuationIds);
    },
    enabled: allValuations.length > 0,
    retry: false,
    staleTime: 10 * 60 * 1000, // 10 minutes cache
    gcTime: 30 * 60 * 1000, // 30 minutes garbage collection
  });

  // Update displayed valuations with notes from React Query cache
  useEffect(() => {
    if (bulkNotesData) {
      // Ensure bulkNotesData is a Map (React Query may deserialize it as a plain object)
      let notesMap: Map<string, Note | null>;
      if (bulkNotesData instanceof Map) {
        notesMap = bulkNotesData;
      } else if (typeof bulkNotesData === 'object' && bulkNotesData !== null) {
        // Convert plain object to Map if React Query deserialized it
        notesMap = new Map(Object.entries(bulkNotesData as Record<string, Note | null>));
      } else {
        notesMap = new Map<string, Note | null>();
      }

      // Store notes in React Query cache individually for easy lookup
      notesMap.forEach((note, valuationId) => {
        queryClient.setQueryData([AttioQueryKeys.BULK_NOTES, valuationId], note);
      });
      
      updateDisplayedValuationsWithNotes(notesMap);
      setIsInitialLoadComplete(true);
    } else if (allValuations.length > 0 && !isLoadingBulkNotes) {
      // No notes data and not loading - set default
      setDefaultNotes();
      setIsInitialLoadComplete(true);
    }
  }, [bulkNotesData, allValuations.length, isLoadingBulkNotes, updateDisplayedValuationsWithNotes, setDefaultNotes, queryClient]);

  // Cancel ongoing bulk requests when pauseBatchLoading becomes true
  useEffect(() => {
    if (pauseBatchLoading && bulkAbortControllerRef.current) {
      bulkAbortControllerRef.current.abort();
      bulkAbortControllerRef.current = null;
      setIsLoadingAdditionalData(false);
    }
  }, [pauseBatchLoading]);

  // Load all additional data in bulk (single batch instead of many)
  useEffect(() => {
    // Pause bulk loading if flag is set (e.g., when modal is open)
    if (pauseBatchLoading) {
      return;
    }

    // Only load once when initial data is complete and not already loaded
    if (isInitialLoadComplete && !additionalDataLoaded && allValuations.length > 0) {
      loadAllAdditionalData();
    }
  }, [isInitialLoadComplete, additionalDataLoaded, allValuations.length, pauseBatchLoading, loadAllAdditionalData]);

  // Functions to update individual valuations with cache invalidation
  const updateValuation = useCallback((updatedValuation: SimplifiedAttioValuation) => {
    // Update displayed valuations optimistically
    setDisplayedValuations(prevValuations => 
      prevValuations.map(valuation => 
        valuation.record_id === updatedValuation.record_id 
          ? { ...valuation, ...updatedValuation }
          : valuation
      )
    );

    // Invalidate and refetch the main query to ensure consistency
    queryClient.invalidateQueries({
      queryKey: [AttioQueryKeys.VALUATIONS_ALL, userRecordId, filters, orderBy, order]
    });
  }, [queryClient, userRecordId, filters, orderBy, order]);

  const updateValuationStatus = useCallback((recordId: string, newStatus: string) => {
    setDisplayedValuations(prevValuations => 
      prevValuations.map(valuation => 
        valuation.record_id === recordId 
          ? { ...valuation, status: newStatus }
          : valuation
      )
    );

    setAllValuations(prevValuations => 
      prevValuations.map(valuation => 
        valuation.record_id === recordId 
          ? { ...valuation, status: newStatus }
          : valuation
      )
    );
    
    // Mark that status was just changed to trigger delayed sorting if sorting by status
    setStatusJustChanged(true);
  }, []);

  const updateValuationNotes = useCallback((recordId: string, newNotes: string) => {
    // Create updated note object
    const updatedNote: Note = {
      id: 0,
      content: newNotes,
      author_id: 0,
      author_email: '',
      author_name: '',
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };

    // Update React Query cache for this specific note
    queryClient.setQueryData([AttioQueryKeys.BULK_NOTES, recordId], updatedNote);

    // Update displayed valuations optimistically
    setDisplayedValuations(prevValuations => 
      prevValuations.map(valuation => 
        valuation.record_id === recordId 
          ? { 
            ...valuation, 
            note: updatedNote
          }
          : valuation
      )
    );

    // Invalidate cache to ensure data consistency
    queryClient.invalidateQueries({
      queryKey: [AttioQueryKeys.VALUATIONS_ALL, userRecordId, filters, orderBy, order]
    });

    // Invalidate additional data cache for this specific record
    queryClient.invalidateQueries({
      queryKey: [AttioQueryKeys.ADDITIONAL_DATA],
      predicate: query => {
        const {queryKey} = query;
        if (Array.isArray(queryKey) && queryKey[0] === AttioQueryKeys.ADDITIONAL_DATA) {
          const recordIds = queryKey[1]?.toString().split(',') || [];
          return recordIds.includes(recordId);
        }
        return false;
      }
    });
  }, [queryClient, userRecordId, filters, orderBy, order]);

  const updateValuationEngagementManager = useCallback(async (
    recordId: string, 
    engagementManagerOption: EngagementManagerOption
  ) => {
    // Use the engagement manager service to load individual data (for updates only)
    const teamService = new AttioTeamService();
    const managerData = await teamService.getTeamMember(engagementManagerOption.targetRecordId);

    const engagementManagerData = {
      name: managerData.name,
      imageSrc: managerData.imageSrc,
    };
    
    queryClient.setQueryData(
      [AttioQueryKeys.ENGAGEMENT_MANAGER, engagementManagerOption.targetRecordId],
      engagementManagerData
    );

    setEngagementManagersCache(prevCache => {
      const newCache = new Map(prevCache);
      newCache.set(engagementManagerOption.targetRecordId, engagementManagerData);
      return newCache;
    });

    setDisplayedValuations(prevValuations => 
      prevValuations.map(valuation => 
        valuation.record_id === recordId 
          ? { 
            ...valuation, 
            engagement_manager: engagementManagerOption.name,
            engagement_manager_image_src: engagementManagerOption.imageSrc,
            engagement_manager_id: engagementManagerOption.targetRecordId,
          }
          : valuation
      )
    );
  }, [queryClient]);

  // Function to clear all caches (useful for manual refresh)
  const clearAllCaches = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: [AttioQueryKeys.VALUATIONS_ALL]
    });
    queryClient.invalidateQueries({
      queryKey: [AttioQueryKeys.ADDITIONAL_DATA]
    });
    queryClient.invalidateQueries({
      queryKey: [AttioQueryKeys.BULK_NOTES]
    });
    queryClient.invalidateQueries({
      queryKey: [AttioQueryKeys.OTHER_CONSULTANT]
    });
    queryClient.invalidateQueries({
      queryKey: [AttioQueryKeys.ENGAGEMENT_MANAGER]
    });
    localStorage.removeItem(VALUATIONS_SORT_KEY);
    setDisplayedValuations([]);
    setAllValuations([]);
    setIsInitialLoadComplete(false);
    setAdditionalDataLoaded(false);
  }, [queryClient]);

  return {
    // Data
    rows: displayedValuations,
    totalCount,
    staffUsers,
    isLoadingStaffUsers,
    setSelectedUserEmail,
    selectedUserEmail,

    // Loading states
    isLoading: isLoadingUserRecord || isLoadingAllValuations || isLoadingBulkNotes || (!isInitialLoadComplete && allValuations.length > 0),
    isLoadingAdditionalData,
    isError: false,
    error: null,
    
    // Update functions
    updateValuation,
    updateValuationStatus,
    updateValuationNotes,
    updateValuationEngagementManager,
    clearAllCaches,
  };
};
